import React from 'react';
import { useParams, Switch } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Spinner } from '@fluentui/react-components';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import Page from '../common/page/Page';
import { IEntityTerminationParams } from '../common/ParamTypes';
import { GetCompanyByCompanyCode } from '../../utils/statApi/CompaniesApi';
import EntityTerminationEligibilityReview from './eligibilityReview/EntityTerminationEligibilityReview';
import EntityTerminationHeader from './EntityTerminationHeader';
import EntityTerminationLegalInProcessPage from './legalInProcess/EntityTerminationLegalInProcessPage';
import EntityTerminationBlockerPage from './blocker/EntityTerminationBlockerPage';
import EntityTerminationFinanceIncompletePage from './financeIncomplete/EntityTerminationFinanceIncompletePage';
import EntityTerminationOverviewPage from './overview/EntityTerminationOverviewPage';

interface IEntityTerminationDetailPageProps {
  baseRoute: string;
}

const EntityTerminationDetailPage = (props: IEntityTerminationDetailPageProps): JSX.Element => {
  const { baseRoute } = props;
  const { companyCode } = useParams<IEntityTerminationParams>();

  // Fetch company details using the companyCode
  const { data, loading, error } = useQuery(GetCompanyByCompanyCode, {
    variables: { companyCode },
    fetchPolicy: 'cache-and-network',
  });

  const company = data?.company;

  return (
    <>
      <EntityTerminationHeader
        showBackToListButton
        companyCode={companyCode}
        entityLegalName={company?.entityLegalName}
        loading={loading}
        baseRoute={baseRoute}
      />
      <LoadingErrorMessage loading={loading} error={error} />
      {loading && <Spinner aria-label="Loading" />}
      <Switch>
        <Page
          path={`/${baseRoute}/:companyCode/eligibility-review`}
          exact
          title={`${company?.companyCode || companyCode} | Eligibility Review`}
          render={(): JSX.Element => (
            <EntityTerminationEligibilityReview
              loading={loading}
              companyCode={company?.companyCode || companyCode}
              companyId={company?.id}
            />
          )}
        />
        <Page
          path={`/${baseRoute}/:companyCode/legal-in-process`}
          exact
          title={`${company?.companyCode || companyCode} | Legal In Process`}
          render={(): JSX.Element => (
            <EntityTerminationLegalInProcessPage
              loading={loading}
              companyCode={company?.companyCode || companyCode}
              details={{
                accountingPlanLink: company?.accountingPlanStarted,
              }}
            />
          )}
        />
        <Page
          path={`/${baseRoute}/:companyCode/blocker`}
          exact
          title={`${company?.companyCode || companyCode} | Blocker`}
          render={(): JSX.Element => (
            <EntityTerminationBlockerPage
              loading={loading}
              companyCode={company?.companyCode || companyCode}
            />
          )}
        />
        <Page
          path={`/${baseRoute}/:companyCode/finance-incomplete`}
          exact
          title={`${company?.companyCode || companyCode} | Finance Incomplete`}
          render={(): JSX.Element => (
            <EntityTerminationFinanceIncompletePage
              loading={loading}
              companyCode={company?.companyCode || companyCode}
            />
          )}
        />
        <Page
          path={`/${baseRoute}/:companyCode/overview`}
          exact
          title={`${company?.companyCode || companyCode} | Overview`}
          render={(): JSX.Element => (
            <EntityTerminationOverviewPage
              loading={loading}
              companyCode={company?.companyCode || companyCode}
            />
          )}
        />
      </Switch>
    </>
  );
};

export default EntityTerminationDetailPage;
